// Pure helpers for the mock counters. Everything is a function of `now`, so a
// reload never resets a number - the counters are derived, not stored.

// US federal holidays (observed) from the Atoll counter's start date onward.
const HOLIDAYS = new Set([
  '2026-09-07', // Labor Day
  '2026-10-12', // Indigenous Peoples' / Columbus Day
  '2026-11-11', // Veterans Day
  '2026-11-26', // Thanksgiving
  '2026-12-25', // Christmas
  '2027-01-01', // New Year's Day
  '2027-01-18', // MLK Day
  '2027-02-15', // Presidents' Day
  '2027-05-31', // Memorial Day
  '2027-06-18', // Juneteenth (observed)
  '2027-07-05', // Independence Day (observed)
  '2027-09-06', // Labor Day
  '2027-10-11', // Indigenous Peoples' / Columbus Day
  '2027-11-11', // Veterans Day
  '2027-11-25', // Thanksgiving
  '2027-12-24', // Christmas (observed)
  '2027-12-31', // New Year's Day 2028 (observed)
]);

export function dateKey(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function isWorkday(date) {
  const day = date.getDay();
  if (day === 0 || day === 6) return false;
  return !HOLIDAYS.has(dateKey(date));
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Workdays strictly after `from` and up to and including `to`. */
export function workdaysBetween(from, to) {
  let count = 0;
  const cursor = startOfDay(from);
  const last = startOfDay(to);
  while (cursor < last) {
    cursor.setDate(cursor.getDate() + 1);
    if (isWorkday(cursor)) count += 1;
  }
  return count;
}

// --- Counter 2: Atoll slowdowns -------------------------------------------
// 32 on Aug 26, 2026, then one more every workday.

export const ATOLL_START_COUNT = 32;
export const ATOLL_EPOCH = new Date(2026, 7, 26); // Aug 26, 2026, local

export function atollSlowdowns(now) {
  return ATOLL_START_COUNT + workdaysBetween(ATOLL_EPOCH, now);
}

// --- Counter 3: data-bug annoyances ---------------------------------------
// 500,000 as of the start of Aug 26, 2026, and one more every five seconds
// forever after. Runs on wall-clock time, weekends included; the data does not
// take days off, so neither does the annoyance.

export const DATA_BUG_BASE = 500000;
export const DATA_BUG_EPOCH = Date.UTC(2026, 7, 26, 0, 0, 0);
export const DATA_BUG_PERIOD_MS = 5000;

export function dataBugAnnoyances(now) {
  return DATA_BUG_BASE + Math.max(0, Math.floor((now - DATA_BUG_EPOCH) / DATA_BUG_PERIOD_MS));
}

// --- Counter 4: missing-Databricks-table eye rolls ------------------------
// Accrues from March 2, 2026. Fast during working hours (one roll every 137
// seconds), slow but never zero outside them - the tables go missing overnight
// too, they just get discovered in the morning.

export const EYE_ROLL_EPOCH = new Date(2026, 2, 2); // March 2, 2026, local
const WORK_START_HOUR = 9;
const WORK_END_HOUR = 18;
const WORK_SECONDS_PER_ROLL = 137;
const OFF_SECONDS_PER_ROLL = 900;

/** Seconds of weekday 09:00-18:00 time between two instants. */
export function workingSecondsBetween(from, to) {
  if (to <= from) return 0;
  let total = 0;
  const cursor = startOfDay(from);
  const end = to.getTime();
  while (cursor.getTime() < end) {
    if (isWorkday(cursor)) {
      const open = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), WORK_START_HOUR).getTime();
      const close = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), WORK_END_HOUR).getTime();
      const overlap = Math.min(close, end) - Math.max(open, from.getTime());
      if (overlap > 0) total += overlap;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return total / 1000;
}

export function databricksEyeRolls(now) {
  const nowDate = new Date(now);
  if (nowDate <= EYE_ROLL_EPOCH) return 0;
  const totalSeconds = (nowDate.getTime() - EYE_ROLL_EPOCH.getTime()) / 1000;
  const workSeconds = workingSecondsBetween(EYE_ROLL_EPOCH, nowDate);
  const offSeconds = Math.max(0, totalSeconds - workSeconds);
  return Math.floor(workSeconds / WORK_SECONDS_PER_ROLL + offSeconds / OFF_SECONDS_PER_ROLL);
}

export function isWorkingHours(now) {
  const date = new Date(now);
  const hour = date.getHours();
  return isWorkday(date) && hour >= WORK_START_HOUR && hour < WORK_END_HOUR;
}

// --- Counter 5: sighs at stupid messages ----------------------------------
// The debt-clock counter. Runs from March 2, 2026 at a rate absurd enough that
// the low digits are a blur at 50ms - which is the point. Stupid messages are
// not a trickle.

export const SIGH_EPOCH = Date.UTC(2026, 2, 2, 0, 0, 0); // March 2, 2026 00:00 UTC
export const SIGH_BASE = 12000000;
export const SIGH_RATE_PER_SECOND = 4201;

export function stupidMessageSighs(now) {
  const elapsedSeconds = Math.max(0, (now - SIGH_EPOCH) / 1000);
  return SIGH_BASE + Math.floor(elapsedSeconds * SIGH_RATE_PER_SECOND);
}
